import React, { useState } from 'react'
import styled from 'styled-components'
import { DetectBlur } from '@/containers/util-containers/DetectBlur'
import { DateRangePickerMonthView } from './DateRangePickerMonthView'
import { DateRangePickerYearView } from './DateRangePickerYearView'
import { isDisabled } from './util'
import type { DatePickerProps } from '@/components_deprecated/DatePicker/types'
import { GRANULARITY } from '@/components_deprecated/DatePicker/types'
import { format } from '@/utils/date-fns'
import { addHours, addMinutes, isSameDay } from 'date-fns'
import { useTranslate } from '@ur-solutions/react-hooks'
import { TimePicker } from '../../TimePicker'

const Wrapper = styled.div`
  font-size: 14px;
  position: relative;
  font-weight: 500;
`

const InputDiv = styled.div<{ open: boolean; grayed?: boolean }>`
  ${props =>
    props.open ? `border-radius: 12px 12px 0 0;` : `border-radius: 12px;`}
  border-color: ${props => props.theme.colors.gray9};
  background: ${props => props.grayed && props.theme.colors.quaternary};

  width: 100%;
  font-size: 1.2rem !important;
  padding: 0 calc(4px + 0.6rem);
  min-height: 3rem;
  line-height: 3rem;
  border: 1px solid ${props => props.theme.colors.gray9};
  user-select: none;
`

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  & > div {
    display: flex;
    gap: 1rem;
    flex-direction: row;
  }

  .date-input {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
  }

  & > div > span {
    font-weight: 600;
    font-size: 1rem;
    color: ${props => props.theme.colors.gray5};
  }
`

const TimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  & > span {
    font-weight: 600;
    font-size: 1rem;
    color: ${props => props.theme.colors.gray5};
  }
`

export const DateRangePicker: React.JSX.CFC<
  DatePickerProps<[Date, Date]> & {
    grayed?: boolean
    normalizeRangeFromToday?: boolean
    labelStyle?: React.CSSProperties
    label?: string
  }
> = ({
  onChange,
  value,
  displayFormat = 'dd.MM.yyyy',
  // TODO unused?
  error: _error,
  label: _label,
  grayed = true,
  showTime = false,
  normalizeRangeFromToday = false,

  disabledDates,
  disableAfter,
  disableBefore,
}) => {
  const translations = useTranslate({
    date: 'common.date',
    dataFrom: 'common.data-from',
    dataTo: 'common.data-to',
  })
  const [monthPivot, setMonthPivot] = useState(value[0])
  const [yearPivot, setYearPivot] = useState(monthPivot)
  const [dateRange, setDateRange] = useState<[Date, Date]>(value)

  const [timeFrom, setTimeFrom] = useState<Date>(value[0])
  const [timeTo, setTimeTo] = useState<Date>(value[1])

  const initialGranularity = GRANULARITY.MONTH

  const [granularity, setGranularity] = useState(initialGranularity)

  const [open, setOpen] = useState(false)

  const isDisabledCallback = (date: Date): boolean => {
    return isDisabled(date, disableBefore, disableAfter, disabledDates)
  }

  const handleRangeChanged = (newDateRange: [Date, Date], close = true) => {
    const [start, end] = newDateRange

    const startWithTime = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
      showTime ? timeFrom.getHours() : 0,
      showTime ? timeFrom.getMinutes() : 0
    )

    const endWithTime = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate(),
      showTime ? timeTo.getHours() : 23,
      showTime ? timeTo.getMinutes() : 59
    )

    onChange([startWithTime, endWithTime])
    setDateRange([startWithTime, endWithTime])

    if (close) setOpen(false)
  }

  const handleInputKeyDown = (event: React.KeyboardEvent<Element>): void => {
    if (event.key === 'Tab') {
      setOpen(false)
    }
  }

  const handleClickOutside = (): void => {
    setOpen(false)
    if (isSameDay(dateRange[0], dateRange[1])) {
      normalizeRangeFromToday
        ? onChange([new Date(), dateRange[1]])
        : onChange([dateRange[0], addMinutes(addHours(dateRange[0], 23), 59)])
    }
  }

  return (
    <Wrapper>
      <DetectBlur
        active
        detectEscape
        disableTouchEnd
        onClickOutside={handleClickOutside}
      >
        <InputWrapper>
          {showTime && (
            <div>
              <TimeWrapper>
                <span>{translations.dataFrom}</span>
                <TimePicker
                  value={timeFrom}
                  height="3rem"
                  onChange={setTimeFrom}
                />
              </TimeWrapper>
              <TimeWrapper>
                <span>{translations.dataTo}</span>
                <TimePicker value={timeTo} height="3rem" onChange={setTimeTo} />
              </TimeWrapper>
            </div>
          )}
          <div className="date-input">
            {/* <span>{label ? label : translations.date}</span> */}
            <InputDiv
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(!open)}
              onChange={() => {}}
              onKeyDown={handleInputKeyDown}
              open={open}
              grayed={grayed}
            >
              {isSameDay(dateRange[0], dateRange[1])
                ? format(dateRange ? dateRange[0] : value[0], displayFormat)
                : format(dateRange ? dateRange[0] : value[0], displayFormat) +
                  ' - ' +
                  format(dateRange ? dateRange[1] : value[1], displayFormat)}
            </InputDiv>
          </div>
        </InputWrapper>
        {open && granularity === GRANULARITY.MONTH && (
          <DateRangePickerMonthView
            dateRange={dateRange}
            monthPivot={monthPivot}
            setMonthPivot={setMonthPivot}
            handleRangeChanged={handleRangeChanged}
            isDisabled={isDisabledCallback}
            onChangeGranularity={() => setGranularity(GRANULARITY.YEAR)}
            grayed={grayed}
          />
        )}
        {open && granularity === GRANULARITY.YEAR && (
          <DateRangePickerYearView
            dateRange={dateRange}
            yearPivot={yearPivot}
            setYearPivot={setYearPivot}
            setMonthPivot={setMonthPivot}
            isDisabled={isDisabledCallback}
            onChangeGranularity={() => setGranularity(GRANULARITY.MONTH)}
            grayed={grayed}
          />
        )}
      </DetectBlur>
    </Wrapper>
  )
}
